import { useMemo, useState } from 'react'
import CloseIcon from '@presentation/assets/procedureCharts/close.png'
import { Position } from 'geojson'

import { InjectionTokens } from '@/controller/tokens'

import { MapboxCoordinates } from '@/domain/protocols/Coordinates'
import { ProceduresState } from '@/domain/states/ProceduresState'

import { stackIds } from '../../containers/MapStackPresenter'
import { ChartEdge, MapChartOpacitySlider } from '../MapChartOpacitySlider'
import { MapRasterLayer } from '../MapView/MapRasterLayer'
import { MapRasterSource } from '../MapView/MapRasterSource'
import { MapSymbolLayer } from '../MapView/MapSymbolLayer'
import { MapSymbolSource } from '../MapView/MapSymbolSource'

import { useInjection } from '@/presentation/contexts/InjectionContext'

// The chart's on-map controls (close button and opacity slider) only show from this zoom on
const CHART_CONTROLS_MIN_ZOOM = 6

interface MapProcedureProps {
  url: string
  boundaries: [Position, Position, Position, Position]
}

function MapProcedure(props: MapProcedureProps) {
  const { url, boundaries } = props

  const proceduresState = useInjection<ProceduresState>(InjectionTokens.ProceduresState)

  const [opacity, setOpacity] = useState<number>(1)

  const formattedBoundaries = useMemo(() => {
    return boundaries.map((position) => {
      return [position[1], position[0]]
    }) as MapboxCoordinates
  }, [boundaries])

  const closeButtonCoords = useMemo(() => {
    return formattedBoundaries[1]
  }, [formattedBoundaries])

  // Along the top edge of the chart's image (top-left → top-right), from the
  // corner opposite the close button towards it, however the chart is turned.
  const opacitySliderEdge = useMemo<ChartEdge>(() => {
    return [formattedBoundaries[0], formattedBoundaries[1]]
  }, [formattedBoundaries])

  const ids = {
    source: {
      closeButton: 'map-procedure-chart-close-button-source',
      chart: 'map-procedure-chart-source'
    },
    layer: {
      closeButton: 'map-procedure-chart-close-button-layer',
      chart: 'map-procedure-chart-layer'
    }
  }

  return (
    <>
      <MapChartOpacitySlider
        edge={opacitySliderEdge}
        value={opacity}
        onChange={setOpacity}
        minZoom={CHART_CONTROLS_MIN_ZOOM}
      />
      <MapSymbolSource
        id={ids.source.closeButton}
        data={{
          type: 'Feature',
          properties: {},
          geometry: {
            coordinates: closeButtonCoords,
            type: 'Point'
          }
        }}
      >
        <MapSymbolLayer
          id={ids.layer.closeButton}
          sourceId={ids.source.closeButton}
          imageSrc={CloseIcon}
          icon={'close'}
          layout={{
            'icon-size': 0.25
          }}
          minZoomLevel={CHART_CONTROLS_MIN_ZOOM}
          onSymbolClick={() => {
            proceduresState.setPresentedProcedure(null)
          }}
          beforeLayerId={stackIds.procedureChartButtons}
        />
      </MapSymbolSource>
      <MapRasterSource id={ids.source.chart} url={url} coordinates={formattedBoundaries}>
        <MapRasterLayer
          id={ids.layer.chart}
          sourceId={ids.source.chart}
          opacity={opacity}
          beforeLayerId={stackIds.procedureChart}
        />
      </MapRasterSource>
    </>
  )
}

export { MapProcedure }
